import { useEffect, useState } from "react";

const NUDGE_COOLDOWN_MS = 8 * 60 * 60 * 1000;

function resolveDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") {
    const resolved = value.toDate();
    return Number.isFinite(resolved?.getTime?.()) ? resolved : null;
  }
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

// eslint-disable-next-line react-refresh/only-export-components -- intentional: colocated test/util export, not a component
export function getNudgeCooldownRemaining(lastNudgeAt, nowMs = Date.now()) {
  const lastNudgeDate = resolveDate(lastNudgeAt);
  if (!lastNudgeDate) return 0;

  const elapsed = nowMs - lastNudgeDate.getTime();
  return Math.max(0, NUDGE_COOLDOWN_MS - elapsed);
}

function formatCooldown(remainingMs) {
  const totalMinutes = Math.ceil(remainingMs / (60 * 1000));
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (hours <= 0) return `${mins}m`;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

export function PollNudgeButton({
  onClick,
  sending = false,
  cooldownRemainingMs = 0,
  lastNudgeAt = null,
  disabled = false,
  disabledReason = "",
  className = "",
}) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const effectiveCooldownRemainingMs = lastNudgeAt
    ? getNudgeCooldownRemaining(lastNudgeAt, nowMs)
    : cooldownRemainingMs;
  const onCooldown = effectiveCooldownRemainingMs > 0;
  const blocked = disabled && Boolean(disabledReason);

  useEffect(() => {
    if (!lastNudgeAt) return undefined;
    const timer = window.setInterval(() => setNowMs(Date.now()), 30 * 1000);
    return () => window.clearInterval(timer);
  }, [lastNudgeAt]);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || sending || onCooldown}
      className={`rounded-full px-2 py-0.5 text-xs font-semibold transition-colors ${
        onCooldown || blocked
          ? "border border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed dark:border-slate-600 dark:bg-slate-700 dark:text-slate-500"
          : "border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
      } ${sending ? "opacity-50" : ""} ${className}`.trim()}
      title={
        blocked
          ? disabledReason
          : onCooldown
          ? "Nudge is on cooldown"
          : "Send a reminder to participants who haven't voted"
      }
    >
      {sending
        ? "Sending..."
        : blocked
          ? "Auto-nag is on"
          : onCooldown
            ? formatCooldown(effectiveCooldownRemainingMs)
            : "Nudge participants"}
    </button>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- intentional: colocated test/util export, not a component
export const __test__ = {
  resolveDate,
  formatCooldown,
  NUDGE_COOLDOWN_MS,
};
